<template>
  <div>
    <div>
      <p class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
        {{
          calculateFirstTime
            ? "Is your company required to carry Workers' Compensation Insurance?"
            : "Confirm whether your company is required to carry Workers' Compensation Insurance."
        }}
      </p>
      <div v-if="calculateFirstTime">
        <div class="flex pb-24">
          <div class="my-1 mr-1 w-full">
            <radio-button
              item="Yes"
              :active="data.workers_compensation === 'Yes'"
              :handle-select="handleSelect"
              name="Yes"
            />
          </div>
          <div class="my-1 ml-1 w-full">
            <radio-button
              item="No"
              :active="data.workers_compensation === 'No'"
              :handle-select="handleSelect"
              name="No"
            />
          </div>
        </div>
      </div>
      <div v-else>
        <p
          class="mb-2 text-left text-2xs font-bold uppercase text-gray-over-dark"
        >
          Workers' Compensation
        </p>
        <p class="text-left text-dark-gray">
          {{ data.workers_compensation }}
        </p>
      </div>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
      @exit-modal="emitExitModal"
    />
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import { useStore } from 'vuex';
import { computed } from 'vue';
import RadioButton from '@/components/RadioButton.vue';

export default {
  name: 'InsuranceStepFour',
  components: {
    RadioButton,
    FormNavigationButtons,
  },
  props: {
    data: Object,
    updateField: Function,
    nextStep: Function,
    formPosition: Number,
    title: String,
    stepName: String,
    skipStep: Function,
    screenSize: String,
  },
  setup(_, { emit }) {
    const store = useStore();
    const componentIdentifier = 'Insurance-4';
    const formRouter = useFormRouter(componentIdentifier);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const firstTime = computed(
      () =>
        store.state.firstTime?.[
          formRouter.componentStep.value?.firstTimeIdentifier
        ]
    );
    const buttonsToDisplay = computed(() => {
      if (firstTime.value) return 'continue-save';
      return 'continue-edit';
    });
    const isButtonDisabled = false;

    const hasFileCargo = computed(() => {
      const cargo = store.state.insurance.insurancePolicies?.find(
        (insurance) =>
          insurance?.is_type === 'motor_truck_cargo' &&
          insurance?.status === 'active'
      );
      return !!cargo;
    });
    const hasFileAuto = computed(() => {
      const auto = store.state.insurance.insurancePolicies?.find(
        (insurance) =>
          insurance?.is_type === 'automobile_liability' &&
          insurance?.status === 'active'
      );
      return !!auto;
    });

    const handleNext = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToNextStep();
    };

    // backChecks: Checks to be reviewed when the user clicks the Back button (but before any store.state.formRoutes.activeStep changes)
    const backCheck1 = computed(() => !hasFileAuto.value && hasFileCargo.value);

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );

      if (backCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is TRUE (No auto policy), moving to Insurance-1'
        );
        return formRouter.moveToStepByIdentifier('Insurance-1');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is FALSE (either has auto policy or no cargo policy)'
        );
      }

      formRouter.moveToPreviousStep();
    };

    const handleEdit = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleEdit' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.editActiveStep();
    };

    const emitExitModal = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'emitExitModal' called"
      );
      emit('exit-modal');
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      handleEdit,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      emitExitModal,
    };
  },
  data() {
    return {
      items: [],
    };
  },
  computed: {
    calculateFirstTime() {
      if (this.firstTime) {
        return true;
      } else if (
        !this.$store.state.carriers.carrierData.workers_comp_selections?.id
      ) {
        return true;
      } else {
        return false;
      }
    },
  },
  watch: {
    stepName: function (val) {
      if (val === 'Insurance-4') {
        // initially set the togglebutton
        if (!this.data.workers_comp_selections?.id) {
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'InsuranceStepFour #1',
          });
        } else {
          this.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'InsuranceStepFour #2',
          });
        }

        const { workers_comp_selections } =
          this.$store.state.carriers.carrierData;

        if (workers_comp_selections?.id) {
          this.updateField(
            'workers_compensation',
            workers_comp_selections?.has_workers_comp ? 'Yes' : 'No'
          );
          this.updateFirstTime(false);
        } else {
          this.updateFirstTime(true);
        }
      }
    },
  },
  methods: {
    handleSelect(e) {
      if (e.target.value === 'No') {
        this.$store.dispatch('onboardingPacket/setOnboardingBody', {
          insurance_workers_comp_selection: {
            has_workers_comp: 0,
          },
        });
      } else {
        this.$store.dispatch('onboardingPacket/setOnboardingBody', {
          insurance_workers_comp_selection: {
            has_workers_comp: 1,
          },
        });
      }

      this.updateField('workers_compensation', e.target.name);
      this.$store.commit('toggleDisabledButton', {
        bool: false,
        source: 'InsuranceStepFour #3',
      });
    },
    async updateFirstTime(firstTime) {
      await this.$store.commit('firstTime/updateStepFirstTime', {
        step: 'Insurance',
        value: firstTime,
      });
    },
  },
};
</script>
